import type { SeriesRef, StudyRef } from '../dicomweb';
import type { ViewerShell } from '../shell';
import { isImageSeries } from '../study';
import * as sync from '../sync';
import type { HangingProtocol, ProtocolViewport } from './client';
import { risClient } from './session';

/**
 * Hanging protocols chosen by ris when it issued the ticket (`protocolId` in the viewer URL): layout
 * rows × cols, and per viewport the series of the current study or the prior whose description
 * contains one of the protocol's terms (next unused image series otherwise), with its window.
 * The protocols themselves are managed in the RIS screens (canvas VisorProtocolos).
 */

export const PROTOCOLS_UNAVAILABLE = ' Error al obtener todas las plantillas de usuario ';

function matches(series: SeriesRef, terms: string[]): boolean {
  const description = series.description.toLowerCase();
  return terms.some((term) => description.includes(term.toLowerCase()));
}

/** The series each viewport shows: term match first, else the next image series not used yet. */
export function assignSeries(protocol: HangingProtocol, current: StudyRef, prior: StudyRef | undefined): Array<SeriesRef | undefined> {
  const used = new Set<string>();
  const pick = (viewport: ProtocolViewport): SeriesRef | undefined => {
    const study = viewport.source === 'prior' ? prior : current;
    const candidates = (study?.series ?? []).filter(isImageSeries);
    const chosen = (viewport.series_terms.length ? candidates.find((s) => !used.has(s.seriesInstanceUID) && matches(s, viewport.series_terms)) : undefined)
      ?? candidates.find((s) => !used.has(s.seriesInstanceUID));
    if (chosen) used.add(chosen.seriesInstanceUID);
    return chosen;
  };
  return protocol.viewports.map(pick);
}

async function applyWindow(shell: ViewerShell, index: number, viewport: ProtocolViewport): Promise<void> {
  if (!viewport.window_width || viewport.window_center === null) return;
  const cell = shell.viewports()[index];
  const current = cell && window.cornerstone.getViewport(cell.element);
  if (!cell || !current) return;
  current.voi.windowWidth = viewport.window_width;
  current.voi.windowCenter = viewport.window_center;
  window.cornerstone.setViewport(cell.element, current);
}

/** Applies ris protocol `protocolId`; false when it cannot be read (the default layout stays). */
export async function applyLaunchProtocol(shell: ViewerShell, protocolId: string): Promise<boolean> {
  const client = risClient();
  if (!client) return false;
  let protocol: HangingProtocol | undefined;
  try {
    protocol = (await client.hangingProtocols()).find((p) => p.id === protocolId);
  } catch (error) {
    console.warn('[viewer] hanging protocols unavailable', error);
  }
  if (!protocol) {
    shell.toast(PROTOCOLS_UNAVAILABLE, 'warning');
    return false;
  }
  const [current, prior] = shell.studies();
  const assigned = assignSeries(protocol, current, prior);
  shell.setLayout({ rows: protocol.rows, columns: protocol.cols });
  await Promise.all(assigned.map(async (series, index) => {
    if (!series) return;
    await shell.showSeriesIn(index, series);
    await applyWindow(shell, index, protocol.viewports[index]);
  }));
  if (protocol.sync.includes('position')) sync.setLink(true);
  document.documentElement.dataset.hangingProtocol = protocol.id;
  return true;
}
